// <!-- GRACE:MODULE_START name="ui_landing" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
// <!-- GRACE:DEPENDENCY target="ui_types" -->
import DictationButton from "../DictationButton";
import AudioUploadButton from "../AudioUploadButton";
import AudioPlayer from "../AudioPlayer";
import { ProcessingState } from "../../types/asyncProcessing";

interface HeroSectionProps {
  isRecording: boolean;
  transcription: string;
  processingState: ProcessingState;
  audioUrl: string | null;
  audioBlob: Blob | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onTranscriptionChange: (text: string) => void;
  onFileChange: (file: File) => void;
  onClear: () => void;
  onCopy: () => void;
  onDownload: () => void;
}

// <!-- GRACE:CONTRACT function="HeroSection" -->
export default function HeroSection({
  isRecording,
  transcription,
  processingState,
  audioUrl,
  audioBlob,
  onStartRecording,
  onStopRecording,
  onTranscriptionChange,
  onFileChange,
  onClear,
  onCopy,
  onDownload,
}: HeroSectionProps) {
  const isProcessing = processingState.status === 'uploading' || processingState.status === 'processing';

  return (
    <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
      <div className="grid items-start gap-6 p-6 sm:p-8 lg:grid-cols-2">
        {/* Left: controls */}
        <div>
          <h3 className="text-2xl font-semibold leading-tight">
            Виртуальный ассистент
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            Распознавание аудиозаписи и форматирование записей
          </p>
          
          {/* Audio recording and upload buttons */}
          <div className="mt-6 flex items-center justify-center gap-8">
            <DictationButton
              isRecording={isRecording}
              processingState={processingState}
              onStart={onStartRecording}
              onStop={onStopRecording}
            />
            <AudioUploadButton
              onFileSelect={onFileChange}
              disabled={isProcessing}
            />
          </div>

          {/* Processing status */}
          {processingState.status === 'uploading' && (
            <div className="mt-4 text-center text-sm text-gray-600">
              <p>Загрузка: {processingState.progress}%</p>
            </div>
          )}
          {processingState.status === 'processing' && (
            <div className="mt-4 text-center text-sm text-gray-600">
              <p>
                {processingState.stage === 'converting' && 'Конвертация аудио...'}
                {processingState.stage === 'recognizing' && 'Распознавание по спикерам...'}
                {processingState.stage === 'queued' && 'В очереди на обработку...'}
                {!processingState.stage && 'Обработка аудио...'}
              </p>
              {(processingState.progress ?? 0) > 0 && (
                <p className="text-xs">Прогресс: {processingState.progress}%</p>
              )}
              {(processingState.estimatedTime ?? 0) > 0 && (
                <p className="text-xs">Осталось ~{processingState.estimatedTime}с</p>
              )}
            </div>
          )}
          {processingState.status === 'error' && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              <p className="font-medium">Ошибка:</p>
              <p className="text-xs mt-1">{processingState.error}</p>
            </div>
          )}

          {/* Audio player */}
          {audioUrl && audioBlob && (
            <div className="mt-4">
              <AudioPlayer audioUrl={audioUrl} audioBlob={audioBlob} />
            </div>
          )}

        </div>

        {/* Right: transcription editor */}
        <div className="w-full">
          <div className="mb-2 text-sm font-medium text-gray-700">
            Результат транскрибации (редактируемый)
          </div>
          <textarea
            value={transcription}
            onChange={(e) => onTranscriptionChange(e.target.value)}
            placeholder="Здесь появится текст транскрибации…"
            className="h-56 w-full resize-y rounded-2xl border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
          <div className="mt-2 flex gap-2 text-xs">
            <button
              onClick={onClear}
              className="rounded-lg border px-3 py-1.5 hover:bg-gray-50"
            >
              Очистить
            </button>
            <button
              onClick={onCopy}
              className="rounded-lg border px-3 py-1.5 hover:bg-gray-50"
            >
              Копировать
            </button>
            <button
              onClick={onDownload}
              className="rounded-lg border px-3 py-1.5 hover:bg-gray-50"
            >
              Скачать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// <!-- GRACE:MODULE_END name="ui_landing" -->
